import React, { useState } from "react";
import { useList, useInvalidate } from "@/lib/hooks";
import api, { apiError } from "@/lib/api";
import { PageHeader, Panel, EmptyState, Field } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { ArrowsLeftRight, Buildings, Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function CarrierBrokers() {
  const { data: carriers = [] } = useList("carriers", "/carriers");
  const { data: relations = [] } = useList("carrier-broker-relations", "/carrier-broker-relations");
  const invalidate = useInvalidate();
  const [carrierId, setCarrierId] = useState("");
  const [broker, setBroker] = useState("");
  const [setupDate, setSetupDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  const carrier = carriers.find((c) => c.id === carrierId);
  const list = relations.filter((r) => r.carrier_id === carrierId);
  const inp = "bg-white border-slate-300";

  const add = async () => {
    if (!carrierId) { toast.error("Select a carrier"); return; }
    if (!broker.trim()) { toast.error("Enter a broker name"); return; }
    setSaving(true);
    try {
      await api.post("/carrier-broker-relations", { carrier_id: carrierId, carrier_name: carrier?.carrier_name || "", broker_name: broker.trim(), setup_date: setupDate, status: "active" });
      toast.success("Broker added");
      setBroker(""); invalidate("carrier-broker-relations");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async (id) => { await api.delete(`/carrier-broker-relations/${id}`); invalidate("carrier-broker-relations"); };

  return (
    <div>
      <PageHeader title="Carrier ↔ Broker Relations" subtitle="Record which brokers each carrier is able to work with, and since when" />

      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4">
        <Panel className="p-5 h-fit">
          <Field label="Select Carrier">
            <Select value={carrierId} onValueChange={setCarrierId}>
              <SelectTrigger className={inp} data-testid="cb-carrier-select"><SelectValue placeholder="Choose a carrier" /></SelectTrigger>
              <SelectContent>{carriers.map((c) => <SelectItem key={c.id} value={c.id}>{c.carrier_name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          {carrierId && (
            <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Add a Broker</p>
              <Field label="Broker Name"><Input data-testid="cb-broker" value={broker} onChange={(e) => setBroker(e.target.value)} className={inp} placeholder="e.g. TQL, RXO…" /></Field>
              <Field label="Set-up Date"><Input type="date" data-testid="cb-date" value={setupDate} onChange={(e) => setSetupDate(e.target.value)} className={inp} /></Field>
              <Button onClick={add} disabled={saving} className="w-full bg-blue-600 text-white hover:bg-blue-700 font-semibold" data-testid="cb-add-btn"><Plus size={16} className="mr-1.5" />{saving ? "Adding…" : "Add Broker"}</Button>
            </div>
          )}
        </Panel>

        <Panel className="p-0">
          {!carrierId ? (
            <EmptyState icon={ArrowsLeftRight} title="Select a carrier" hint="Pick a carrier to view and manage the brokers it works with." />
          ) : (
            <>
              <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
                <Buildings size={18} weight="duotone" className="text-blue-600" />
                <h3 className="font-heading font-bold text-slate-900">{carrier?.carrier_name}</h3>
                <span className="text-xs text-slate-500">· works with {list.length} broker{list.length === 1 ? "" : "s"}</span>
              </div>
              {list.length === 0 ? (
                <EmptyState icon={ArrowsLeftRight} title="No brokers yet" hint="Add the first broker this carrier can work with." />
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-200 hover:bg-transparent"><TableHead>Broker</TableHead><TableHead>Set-up Date</TableHead><TableHead>Status</TableHead><TableHead className="text-right"></TableHead></TableRow>
                    </TableHeader>
                    <TableBody>
                      {list.map((r) => (
                        <TableRow key={r.id} className="border-slate-100" data-testid={`cb-rel-${r.id}`}>
                          <TableCell className="font-medium text-slate-900">{r.broker_name}</TableCell>
                          <TableCell className="text-sm text-slate-600">{r.setup_date || "—"}</TableCell>
                          <TableCell><span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full capitalize">{r.status || "active"}</span></TableCell>
                          <TableCell className="text-right"><button onClick={() => remove(r.id)} className="p-1.5 text-slate-400 hover:text-red-500" data-testid={`cb-del-${r.id}`}><Trash size={16} /></button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </>
          )}
        </Panel>
      </div>
    </div>
  );
}
